import * as React from "react"
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
} from "@/components/ui/command"
import { useCommandRegistry, Command } from "@/contexts/CommandRegistryContext"
import { useDebounce } from "@/hooks/useDebounce"

export function CommandMenu() {
    const [open, setOpen] = React.useState(false)
    const { commands, setSearchQuery } = useCommandRegistry()
    const [search, setSearch] = React.useState("")
    const [searchResults, setSearchResults] = React.useState<Command[]>([])
    const debouncedSearch = useDebounce(search, 300)

    React.useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                setOpen((open) => !open)
            }
        }

        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, [])

    // Update the search query in the registry so it can show/hide contextual commands
    React.useEffect(() => {
        setSearchQuery(debouncedSearch)
    }, [debouncedSearch, setSearchQuery])


    React.useEffect(() => {
        const searchCommands = async () => {
            if (!debouncedSearch.trim()) {
                setSearchResults(commands)
                return
            }

            // Local filtering by label, description, and keywords
            const lower = debouncedSearch.toLowerCase()
            const filtered = commands.filter(c => {
                const matchesLabel = c.label.toLowerCase().includes(lower)
                const matchesDescription = c.description?.toLowerCase().includes(lower)
                const matchesKeywords = c.keywords?.some(k => k.toLowerCase().includes(lower))
                const matchesId = c.id.toLowerCase().includes(lower)

                return matchesLabel || matchesDescription || matchesKeywords || matchesId
            })

            setSearchResults(filtered)

            /*
                       try {
                           const response = await fetch('/api/ai/command-search', {
                               method: 'POST',
                               headers: {
                                   'Content-Type': 'application/json',
                               },
                               body: JSON.stringify({
                                   query: debouncedSearch,
                                   commands: commands.map(({ id, label, description }) => ({ id, label, description })),
                               }),
                           })

                           if (!response.ok) {
                               console.error('Failed to search commands')
                               // Fallback to local filtering if API fails?
                               // For now, just show all or maybe nothing?
                               // Let's fallback to simple local filtering
                               const lower = debouncedSearch.toLowerCase()
                               setSearchResults(commands.filter(c =>
                                   c.label.toLowerCase().includes(lower) ||
                                   c.description?.toLowerCase().includes(lower)
                               ))
                               return
                           }

                           const results: { id: string, score: number }[] = await response.json()

                           // Map results back to commands and sort
                           const sortedCommands = results
                               .map(r => commands.find(c => c.id === r.id))
                               .filter((c): c is Command => !!c)

                           setSearchResults(sortedCommands)

                       } catch (error) {
                           console.error('Error searching commands:', error)
                           // Fallback to simple local filtering
                           const lower = debouncedSearch.toLowerCase()
                           setSearchResults(commands.filter(c =>
                               c.label.toLowerCase().includes(lower) ||
                               c.description?.toLowerCase().includes(lower)
                           ))
                       }*/
        }

        searchCommands()

    }, [debouncedSearch, commands])

    const runCommand = React.useCallback((command: () => unknown) => {
        setOpen(false)
        command()
    }, [])

    // Separate contextual (high-scoring) commands from regular commands
    const { contextualCommands, regularCommands } = React.useMemo(() => {
        const contextual: Command[] = []
        const regular: Command[] = []

        searchResults.forEach(cmd => {
            if (cmd.score && cmd.score > 0) {
                contextual.push(cmd)
            } else {
                regular.push(cmd)
            }
        })

        return { contextualCommands: contextual, regularCommands: regular }
    }, [searchResults])

    // Group regular commands while preserving their score-based order
    const groupedCommands = React.useMemo(() => {
        const groups: Record<string, Command[]> = {}

        // Commands are already sorted by score from CommandRegistryContext
        // Group them while preserving that order
        regularCommands.forEach(cmd => {
            const group = cmd.group || 'General'
            if (!groups[group]) {
                groups[group] = []
            }
            groups[group].push(cmd)
        })

        // Sort groups: only prioritize groups that have commands with score > 0
        // This ensures contextual commands only affect order when they're actually contextual
        const sortedGroups = Object.entries(groups).sort(([groupA, cmdsA], [groupB, cmdsB]) => {
            const maxScoreA = Math.max(...cmdsA.map(c => c.score || 0))
            const maxScoreB = Math.max(...cmdsB.map(c => c.score || 0))

            // Only use score for sorting if at least one group has a score > 0
            if (maxScoreA > 0 || maxScoreB > 0) {
                return maxScoreB - maxScoreA
            }

            // Otherwise, maintain original order (by group name alphabetically)
            return groupA.localeCompare(groupB)
        })

        return Object.fromEntries(sortedGroups)
    }, [regularCommands])

    return (
        <CommandDialog open={open} onOpenChange={setOpen} className="w-140">
            <CommandInput
                placeholder="Type a command or search..."
                value={search}
                onValueChange={setSearch}
            />
            <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>

                {/* Contextual commands section - shown at the top when present */}
                {contextualCommands.length > 0 && (
                    <>
                        <CommandGroup heading="Suggestions">
                            {contextualCommands.map((cmd) => (
                                <CommandItem
                                    key={cmd.id}
                                    value={cmd.label}
                                    onSelect={() => runCommand(cmd.action)}
                                >
                                    {cmd.icon}
                                    <span>{cmd.label}</span>
                                    {cmd.shortcut && <CommandShortcut>{cmd.shortcut}</CommandShortcut>}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        {Object.keys(groupedCommands).length > 0 && <CommandSeparator />}
                    </>
                )}

                {/* Regular grouped commands */}
                {Object.entries(groupedCommands).map(([group, cmds]) => (
                    <CommandGroup key={group} heading={group}>
                        {cmds.map((cmd) => (
                            <CommandItem
                                key={cmd.id}
                                value={cmd.label} // Value is still needed for selection but filtering is disabled
                                onSelect={() => runCommand(cmd.action)}
                            >
                                {cmd.icon}
                                <span>{cmd.label}</span>
                                {cmd.shortcut && <CommandShortcut>{cmd.shortcut}</CommandShortcut>}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                ))}
            </CommandList>
        </CommandDialog>
    )
}
